import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle, Phone, Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, servicesRes] = await Promise.all([
          axios.get(`${API}/pricing-plans`),
          axios.get(`${API}/services`)
        ]);
        setPlans(plansRes.data);
        setServices(servicesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="pricing-page">
      {/* Hero Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-heading text-gray-900 mb-4">
              Transparent <span className="text-primary">Pricing</span>
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Fair, competitive pricing for quality landscaping services. 
              Choose a plan or get a custom quote for your specific needs.
            </p>
          </div>

          {/* Monthly Plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {plans.map((plan) => (
              <Card 
                key={plan.id}
                data-testid={`pricing-plan-${plan.id}`}
                className={`bg-white border-gray-200 relative ${
                  plan.highlight 
                    ? 'border-accent border-2 scale-105 shadow-xl' 
                    : 'hover:border-primary hover:shadow-lg'
                } transition-all duration-300`}
              >
                {plan.highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-accent text-black text-xs font-accent px-4 py-1.5 rounded-full flex items-center">
                      <Star className="h-3 w-3 mr-1 fill-black" />
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-0 pt-8">
                  <CardTitle className="text-2xl font-heading text-gray-900">{plan.name}</CardTitle>
                </CardHeader>
                <CardContent className="p-8 text-center">
                  <div className="mb-6">
                    <span className="text-5xl font-heading text-primary">{plan.price}</span>
                    <span className="text-gray-500 text-lg">/{plan.period}</span>
                  </div>
                  <p className="text-gray-600 mb-8">{plan.description}</p>
                  <ul className="space-y-4 mb-8 text-left">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button 
                      data-testid={`pricing-cta-${plan.id}`}
                      className={`w-full font-accent py-6 text-lg ${
                        plan.highlight 
                          ? 'bg-accent hover:bg-accent/90 text-black' 
                          : 'bg-primary hover:bg-primary/90 text-white'
                      }`}
                    >
                      Get Started
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Individual Services */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-heading text-gray-900 text-center mb-12">
              Individual <span className="text-accent">Service Pricing</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => (
                <Card 
                  key={service.id}
                  data-testid={`service-price-${service.id}`}
                  className="bg-white border-gray-200 hover:border-primary/50 hover:shadow-md transition-all"
                >
                  <CardContent className="p-6 flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-heading text-gray-900">{service.name}</h3>
                      <p className="text-gray-500 text-sm">{service.description.substring(0, 50)}...</p>
                    </div>
                    <div className="text-right">
                      <p className="text-accent font-accent text-lg">{service.price}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Price Factors */}
          <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-md">
            <h3 className="text-2xl font-heading text-gray-900 mb-6 text-center">
              What Affects Your Price?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl font-heading text-primary mb-2">Size</div>
                <p className="text-gray-600 text-sm">
                  Lot size and lawn area directly impact service time and pricing.
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-heading text-primary mb-2">Frequency</div>
                <p className="text-gray-600 text-sm">
                  Weekly service plans offer better rates than one-time visits.
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-heading text-primary mb-2">Condition</div>
                <p className="text-gray-600 text-sm">
                  Initial cleanup may be needed for overgrown or neglected properties.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-heading text-white mb-6">
            Need a Custom Quote?
          </h2>
          <p className="text-white/90 mb-8">
            Every property is unique. Contact us for a free, no-obligation assessment 
            and personalized quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                data-testid="pricing-quote-btn"
                size="lg"
                className="bg-accent hover:bg-accent/90 text-black font-accent text-lg px-8"
              >
                Request Free Quote
              </Button>
            </Link>
            <a href="tel:7134999372">
              <Button 
                data-testid="pricing-call-btn"
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-primary font-accent text-lg px-8"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call (713) 499-9372
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
